import {
  makeStyles, shorthands, tokens, Dropdown, Option, Text, Badge,
} from '@fluentui/react-components';
import { useCustomer } from '../context/CustomerContext';

const useStyles = makeStyles({
  bar: {
    display: 'flex', alignItems: 'center', columnGap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalXXL),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    position: 'sticky', top: 0, zIndex: 10,
  },
  group: { display: 'flex', alignItems: 'center', columnGap: tokens.spacingHorizontalS },
  label: {
    color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200,
    textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: tokens.fontWeightSemibold,
  },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  spacer: { flexGrow: 1 },
});

function formatUtc(value: string): string {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toISOString().replace('T', ' ').slice(0, 16);
}

/**
 * Selects the customer and collection every page is scoped to.
 *
 * Sticky, because which customer is on screen is the single most important
 * piece of context in the whole interface, and a consultant with several
 * customers open should never have to guess which one they are reading.
 */
export function CustomerPicker() {
  const styles = useStyles();
  const { tenants, tenant, selectTenant, runs, run, selectRun } = useCustomer();

  if (tenants.length === 0) {
    return (
      <div className={styles.bar}>
        <Text className={styles.muted}>
          No customers registered on this stamp yet.
        </Text>
      </div>
    );
  }

  return (
    <div className={styles.bar}>
      <div className={styles.group}>
        <span className={styles.label}>Customer</span>
        <Dropdown
          aria-label="Customer"
          value={tenant?.displayName ?? ''}
          selectedOptions={tenant ? [tenant.tenantId] : []}
          onOptionSelect={(_, d) => d.optionValue && selectTenant(d.optionValue)}
          style={{ minWidth: '240px' }}
        >
          {tenants.map((t) => (
            <Option key={t.tenantId} value={t.tenantId} text={t.displayName}>
              {t.displayName}
            </Option>
          ))}
        </Dropdown>
      </div>

      <div className={styles.group}>
        <span className={styles.label}>Collection</span>
        {runs.length === 0 ? (
          <Text className={styles.muted}>None yet</Text>
        ) : (
          <Dropdown
            aria-label="Collection"
            value={run ? formatUtc(run.startedUtc) : ''}
            selectedOptions={run ? [run.runId] : []}
            onOptionSelect={(_, d) => d.optionValue && selectRun(d.optionValue)}
            style={{ minWidth: '210px' }}
          >
            {runs.map((r) => (
              <Option key={r.runId} value={r.runId} text={formatUtc(r.startedUtc)}>
                {formatUtc(r.startedUtc)} · {r.status} · {r.findingCount}
              </Option>
            ))}
          </Dropdown>
        )}
      </div>

      {/* A partial collection must never be read as a clean result, so a run
          that failed is labelled wherever it is shown. */}
      {run?.status === 'failed' && (
        <Badge appearance="tint" color="danger">
          This collection failed and is incomplete
        </Badge>
      )}
      {run?.status === 'running' && (
        <Badge appearance="tint" color="warning">Collection still running</Badge>
      )}

      <div className={styles.spacer} />

      {tenant && (
        <Text className={styles.muted}>
          {tenant.homeRegion} · {tenant.frameworks.length === 0
            ? 'global fallback only'
            : tenant.frameworks.map((f) => f.name).join(', ')}
        </Text>
      )}
    </div>
  );
}
